/**
 * Client-side CCCD QR decoder (ZXing-wasm in the browser).
 *
 * Why this exists: the Vietnamese CCCD QR is extremely information-dense
 * (12-digit id + name + DOB + gender + address + issue date, pipe
 * separated) and ZXing is the most robust open-source QR engine for
 * damaged / rotated / low-light codes. Running it in the browser before
 * the upload means a successful scan skips both the network round-trip
 * and the Mistral OCR bill — operators on mobile 5G get their answer in
 * roughly 200 ms instead of 5–10 seconds.
 *
 * Returned pipe format (when present on the card):
 *   soCCCD | soCMT | hoTen | ngaySinh | gioiTinh | diaChi | ngayCap
 *
 * All parts may be empty. Call sites should defensively `clean()` each
 * field and treat absence as "unknown, let server OCR try".
 */
import { BrowserQRCodeReader } from '@zxing/browser';

/** Shared reader — ZXing initialisation is non-trivial; keep one. */
let cachedReader: BrowserQRCodeReader | null = null;
const getReader = (): BrowserQRCodeReader => {
  if (!cachedReader) cachedReader = new BrowserQRCodeReader();
  return cachedReader;
};

/** Load a File into an HTMLImageElement via a blob URL. ZXing's image
 *  decoder accepts either a data URL or an <img>; <img> avoids the 2x
 *  memory spike of base64-encoding the full file. */
const fileToImage = (file: File): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('decode'));
    };
    img.src = url;
    // Revoke after load handler picks it up — the <img> keeps a decoded
    // copy, so the underlying blob can be freed.
    img.addEventListener('load', () => URL.revokeObjectURL(url), { once: true });
  });

/**
 * Try to decode the CCCD QR from a raw File entirely client-side.
 * Returns the decoded text (usually pipe-separated) or null on miss.
 * Never throws — miss is a normal outcome (low-res / no QR / damaged).
 */
export const decodeCccdQrFromFile = async (file: File): Promise<string | null> => {
  try {
    if (!file || !file.type.startsWith('image/')) return null;
    const img = await fileToImage(file);
    const reader = getReader();
    const result = await reader.decodeFromImageElement(img);
    const text = result?.getText?.();
    return text && text.length > 0 ? text : null;
  } catch {
    // @zxing/browser throws NotFoundException when no QR is present —
    // expected. Swallow and fall back to the server pipeline.
    return null;
  }
};

type CccdQrPayload = {
  soCCCD: string;
  soCMT: string;
  hoTen: string;
  ngaySinh: string;
  gioiTinh: string;
  diaChi: string;
  ngayCap: string;
};

/**
 * Parse the Vietnamese CCCD QR pipe format into named fields. The card's
 * QR encoder uses ISO-8859-1 / UTF-8 depending on batch — the decoder
 * hands us a JS string already, so we just split and normalise.
 *
 * Only returns a payload when soCCCD looks valid (12 digits) — this
 * prevents arbitrary QR codes (product packaging, random URLs, etc.)
 * from short-circuiting the CCCD pipeline with garbage data.
 */
export const parseCccdQrPipe = (text: string): CccdQrPayload | null => {
  if (!text) return null;
  const parts = text.split('|').map((s) => s.trim());
  const soCCCD = parts[0] || '';
  if (!/^\d{12}$/.test(soCCCD)) return null;
  const rawHoTen = parts[2] || '';
  // Diagnostic — print codepoints of the decoded name so encoding bugs
  // (e.g. Â reported as À) can be traced back to ZXing's output vs our
  // display. Browser console only; harmless in prod.
  try {
    const codepoints = Array.from(rawHoTen)
      .map((c) => 'U+' + c.codePointAt(0)!.toString(16).toUpperCase().padStart(4, '0'))
      .join(' ');
    // eslint-disable-next-line no-console
    console.log(`[ZXing QR] hoTen raw="${rawHoTen}" codepoints=${codepoints}`);
  } catch {
    /* ignore logging errors */
  }
  return {
    soCCCD,
    soCMT: parts[1] || '',
    hoTen: rawHoTen.toUpperCase(),
    ngaySinh: parts[3] || '',
    gioiTinh: parts[4] || '',
    diaChi: parts[5] || '',
    ngayCap: parts[6] || '',
  };
};
